import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Plus, Edit, Trash2, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEM_TYPES = [
  { value: 'booking', label: 'One-on-One Session' },
  { value: 'test_tier', label: 'Test Tier Access' },
  { value: 'course', label: 'Course Enrollment' },
  { value: 'publication_subscription', label: 'Publication Center Membership' },
  { value: 'donation', label: 'Donation' }
];

const TEST_TIERS = [
  { value: 'basic', label: 'Basic Practice' },
  { value: 'comps_ready', label: 'Comps Ready' },
  { value: 'advanced', label: 'Advanced' }
];

export default function PricingManager() {
  const [pricingList, setPricingList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    item_type: 'booking',
    item_id: 'default',
    amount: 0,
    currency: 'usd',
    description: '',
    enabled: true,
    is_free: false,
    admin_free_access: false
  });

  useEffect(() => {
    loadPricing();
    loadCourses();
  }, []);

  const loadPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing`, {
        withCredentials: true
      });
      setPricingList(response.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingPricing) {
        await axios.put(`${API}/admin/pricing/${editingPricing.id}`, formData, {
          withCredentials: true
        });
        toast.success('Pricing updated!');
      } else {
        await axios.post(`${API}/admin/pricing`, formData, {
          withCredentials: true
        });
        toast.success('Pricing created!');
      }
      
      setShowForm(false);
      setEditingPricing(null);
      setFormData({
        item_type: 'booking',
        item_id: 'default',
        amount: 0,
        currency: 'usd',
        description: '',
        enabled: true,
        is_free: false,
        admin_free_access: false
      });
      loadPricing();
    } catch (error) {
      toast.error('Failed to save pricing');
    }
  };

  const handleEdit = (pricing) => {
    setEditingPricing(pricing);
    setFormData({
      item_type: pricing.item_type,
      item_id: pricing.item_id,
      amount: pricing.amount,
      currency: pricing.currency,
      description: pricing.description,
      enabled: pricing.enabled,
      is_free: pricing.is_free || false,
      admin_free_access: pricing.admin_free_access || false
    });
    setShowForm(true);
  };

  const handleDelete = async (pricingId) => {
    if (!window.confirm('Delete this pricing?')) return;
    
    try {
      await axios.delete(`${API}/admin/pricing/${pricingId}`, {
        withCredentials: true
      });
      toast.success('Pricing deleted');
      loadPricing();
    } catch (error) {
      toast.error('Failed to delete pricing');
    }
  };

  const getItemLabel = (pricing) => {
    switch (pricing.item_type) {
      case 'booking':
        return 'One-on-One Session';
      case 'test_tier':
        const tier = TEST_TIERS.find(t => t.value === pricing.item_id);
        return tier ? tier.label : pricing.item_id;
      case 'course':
        const course = courses.find(c => c.id === pricing.item_id);
        return course ? course.title : pricing.item_id;
      case 'donation':
        return 'Donation';
      default:
        return pricing.item_type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl retro-title text-[#2C1810]">Pricing Manager</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingPricing(null);
            setFormData({
              item_type: 'booking',
              item_id: 'default',
              amount: 0,
              currency: 'usd',
              description: '',
              enabled: true,
              is_free: false,
              admin_free_access: false
            });
          }}
          className="retro-button-small flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Pricing
        </button>
      </div>

      <div className="cassette-card p-6">
        <p className="text-[#6B4423] mb-4">
          Set prices for courses, test tiers, and one-on-one sessions. These prices will be used during checkout.
        </p>

        {/* Pricing List */}
        <div className="space-y-4">
          {pricingList.length === 0 ? (
            <div className="text-center py-12 text-[#6B4423]">
              <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No pricing configured yet. Add your first price setting.</p>
            </div>
          ) : (
            pricingList.map((pricing) => (
              <div key={pricing.id} className="cassette-card p-4 flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-[#2C1810]">{getItemLabel(pricing)}</h3>
                    {!pricing.enabled && (
                      <span className="text-xs px-2 py-1 bg-gray-400 text-white rounded">Disabled</span>
                    )}
                    {pricing.is_free && (
                      <span className="text-xs px-2 py-1 bg-[#4D9E7C] text-white rounded font-bold">FREE</span>
                    )}
                    {pricing.admin_free_access && (
                      <span className="text-xs px-2 py-1 bg-[#E8934D] text-white rounded font-bold">ADMIN FREE</span>
                    )}
                  </div>
                  <p className="text-sm text-[#6B4423] mb-1">{pricing.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-[#E8934D]">
                      {pricing.is_free ? 'Free' : `$${pricing.amount.toFixed(2)}`}
                    </span>
                    {!pricing.is_free && (
                      <span className="text-sm text-[#6B4423] uppercase">{pricing.currency}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(pricing)}
                    className="retro-button-small bg-[#4D9E7C] hover:bg-[#3D7E5C]"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pricing.id)}
                    className="retro-button-small bg-[#E85D4D] hover:bg-[#C84D3D]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pricing Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="cassette-card max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">
                {editingPricing ? 'Edit Pricing' : 'Add New Pricing'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#6B4423] hover:text-[#E85D4D]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Item Type *</label>
                <select
                  value={formData.item_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFormData({
                      ...formData,
                      item_type: newType,
                      item_id: newType === 'test_tier' ? 'basic' : 'default'
                    });
                  }}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                >
                  {ITEM_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {formData.item_type === 'test_tier' && (
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Test Tier *</label>
                  <select
                    value={formData.item_id}
                    onChange={(e) => setFormData({ ...formData, item_id: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    required
                  >
                    {TEST_TIERS.map(tier => (
                      <option key={tier.value} value={tier.value}>{tier.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.item_type === 'course' && (
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Course *</label>
                  <select
                    value={formData.item_id}
                    onChange={(e) => setFormData({ ...formData, item_id: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    required
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.title}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  >
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                    <option value="gbp">GBP</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Brief description of what this payment is for"
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="enabled" className="text-sm font-semibold text-[#6B4423]">
                  Enabled (uncheck to disable this pricing)
                </label>
              </div>

              {formData.item_type === 'test_tier' && (
                <div className="flex items-center gap-2 p-4 bg-[#4D9E7C]/10 border-2 border-[#4D9E7C] rounded">
                  <input
                    type="checkbox"
                    id="is_free"
                    checked={formData.is_free}
                    onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label htmlFor="is_free" className="text-sm font-semibold text-[#2C1810]">
                    🎁 Free Access (Allow all registered users to access this tier without payment)
                  </label>
                </div>
              )}

              {/* Admin Free Access - Available for all item types */}
              <div className="flex items-center gap-2 p-4 bg-[#E8934D]/10 border-2 border-[#E8934D] rounded">
                <input
                  type="checkbox"
                  id="admin_free_access"
                  checked={formData.admin_free_access}
                  onChange={(e) => setFormData({ ...formData, admin_free_access: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="admin_free_access" className="text-sm font-semibold text-[#2C1810]">
                  👑 Admin Free Access (Automatically grant free access to all admin users)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="retro-button flex-1 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingPricing ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
